import debugConstructor from 'debug'
import * as PIXI from 'pixi.js'

import invariant from '../vendor/invariant'

import { EventHandlers } from './eventHandlers'
import { setValue } from './pixi'

const debug = debugConstructor('reconciler')

const noop = () => {}

const EventHandlersList = Object.keys(EventHandlers)

export const CHILDREN = 'children'

/**
 * Reserved props
 *
 * @type {Object}
 */
const RESERVED_PROPS = {
  [CHILDREN]: true,
  parent: true,
  worldAlpha: true,
  worldTransform: true,
  worldVisible: true,
  cacheAsBitmap: true,
}
const reservedPropsList = Object.keys(RESERVED_PROPS)

/**
 * Default display object props
 * See https://github.com/michalochman/react-pixi-fiber/blob/a4dbddbef0ffbf0f563c64d30766ea28222a51ea/src/props.js#L9
 *
 * @type {Object}
 */
export const PROPS_DISPLAY_OBJECT = {
  alpha: 1,
  buttonMode: false,
  cacheAsBitmap: false,
  cursor: null,
  filterArea: null,
  filters: null,
  hitArea: null,
  interactive: false,
  mask: null,
  pivot: { x: 0, y: 0 },
  position: 0,
  renderable: true,
  rotation: 0,
  scale: 1,
  skew: 0,
  transform: null,
  visible: true,
  x: 0,
  y: 0,
}

/**
 * Helper util for fetching the texture from props
 * Can be either texture or image
 *
 * @param {string} elementType
 * @param {PIXI.Texture} texture
 * @param {string|undefined} image
 * @returns {PIXI.Texture}
 */
export const getTextureFromProps = (elementType, props) => {
  const { texture, image } = props
  debug('getTextureFromProps', props)
  if (image) {
    invariant(
      typeof image === 'string',
      elementType + ' image needs to be a string, got `%s`',
      typeof image,
    )
    return PIXI.Texture.from(image)
  }

  invariant(
    texture instanceof PIXI.Texture,
    elementType + ' texture needs to be typeof `PIXI.Texture`',
  )
  return texture
}

export function applyDisplayObjectEvents(instance, oldProps, newProps) {
  const addListener = instance.addEventListener || noop
  const removeListener = instance.removeEventListener || noop
  let hasEventHandlers = false
  for (const evt of EventHandlersList) {
    if (typeof newProps[evt] === 'function') {
      hasEventHandlers = true
    }

    if (oldProps[evt] === newProps[evt]) {
      continue
    }
    const pixiEvt = EventHandlers[evt]
    if (typeof oldProps[evt] === 'function') {
      removeListener.call(instance, pixiEvt, oldProps[evt])
    }
    if (typeof newProps[evt] === 'function') {
      addListener.call(instance, pixiEvt, newProps[evt])
    }
  }
  instance.interactive = 'interactive' in newProps ? newProps.interactive : hasEventHandlers
}

const propsFilter = (key) => !reservedPropsList.includes(key) && !EventHandlersList.includes(key)

/**
 * Apply default props on Display Object instance (which are all components)
 *
 * @param {PIXI.DisplayObject} instance
 * @param {Object} oldProps
 * @param {Object} newProps
 */
export function applyDisplayObjectProps(instance, oldProps, newProps) {
  debug('applyDisplayObjectProps')
  invariant(
    Object.prototype.isPrototypeOf.call(PIXI.DisplayObject.prototype, instance),
    'instance needs to be typeof `PIXI.DisplayObject`, got `%s`',
    typeof instance,
  )

  const newKeys = Object.keys(newProps || {}).filter(propsFilter)

  for (let i = 0; i < newKeys.length; i++) {
    const key = newKeys[i]
    const value = newProps[key]
    setValue(instance, key, value)
  }

  const oldKeys = Object.keys(oldProps).filter(propsFilter)
  for (let i = 0; i < oldKeys.length; i++) {
    const key = oldKeys[i]
    if (!(key in newProps)) {
      if (key in PROPS_DISPLAY_OBJECT) {
        setValue(instance, key, PROPS_DISPLAY_OBJECT[key])
      } else {
        delete instance[key]
      }
    }
  }

  // update event handlers
  applyDisplayObjectEvents(instance, oldProps, newProps)

  // Applying cacheAsBitmap last to get cache with other props updated
  if (oldProps.cacheAsBitmap !== newProps.cacheAsBitmap) {
    instance.cacheAsBitmap = newProps.cacheAsBitmap
  }
}
